import { Component, OnInit, signal, WritableSignal } from "@angular/core";
import { RouterOutlet } from "@angular/router";
import { ModalComponent } from "@shared/components/modal/modal.component";
import { isErudaEnabled } from "@shared/environments/environment";
import { MaintenanceConfigType } from "@shared/types/MaintenanceConfigType";

type WindowWithEruda = Window & typeof globalThis & { eruda: { init: () => void } };

@Component({
    selector: "app-root",
    imports: [RouterOutlet, ModalComponent],
    templateUrl: "./app.component.html",
    styleUrl: "./app.component.css",
})
// Root application component responsible for bootstrapping the application and conditionally loading the Eruda debug console in development.
export class AppComponent implements OnInit {
    protected readonly isMaintenanceModalVisible: WritableSignal<boolean> = signal(false);
    protected readonly maintenanceMessage: WritableSignal<string> = signal("");
    protected readonly maintenanceResponsible: WritableSignal<string> = signal("");

    ngOnInit(): void {
        if (isErudaEnabled) {
            // Load the debug console only in debug builds.
            const script: HTMLScriptElement = document.createElement("script");
            script.src = "https://cdn.jsdelivr.net/npm/eruda";
            script.onload = () => {
                (window as WindowWithEruda).eruda.init();
            };
            document.body.appendChild(script);
        }

        // Fetch the app status configuration from the JSON file.
        this.fetchAppStatusConfig("/app-status.json");
    }

    private async fetchAppStatusConfig(jsonFilePath: string): Promise<void> {
        try {
            const response: Response = await fetch(jsonFilePath);

            if (!response.ok) {
                throw new Error(`Failed to fetch ${jsonFilePath}: ${response.status} ${response.statusText}`);
            }

            const data: MaintenanceConfigType = await response.json();

            this.isMaintenanceModalVisible.set(data.maintenanceMode);
            this.maintenanceMessage.set(data.maintenanceMessage);
            this.maintenanceResponsible.set(data.maintenanceResponsible);
        } catch (error) {
            console.error("Error fetching app status config:", error);
        }
    }
}
